/**
 * The transport row above the dope sheet: playback, frame/fps/length/speed slots, loop + snap,
 * the keying actions with their easing, then the view controls (bone filter, zoom) and the name
 * of the clip the sheet belongs to.
 *
 * Plain DOM widgets; every control dispatches a store action and every value is re-read from the
 * store in `sync()`, so the row can never drift from the state.
 *
 * The row is ONE line at the 1280px layout floor and never wraps (`.tl-transport` is
 * `flex-wrap: nowrap`): wrapping stole 27px of dope sheet at every width ≤1366 (audit A4). That
 * budget is why undo/redo are not here — they are the toolbar's, next to Save — and why loop and
 * snap are icon toggles rather than labelled checkboxes. The clip name is the one elastic item:
 * it ellipsises before anything else gives up width.
 */

import { TWEEN_PRESETS } from '../../shared';
import type { TweenPreset } from '../../shared';
import type { EditorContext } from '../context';
import { selectSelectionEasing, TIMELINE_ZOOM_MAX, TIMELINE_ZOOM_MIN } from '../index';
import type { VisibleBonesFilter } from '../index';
import { icon, type IconName } from '../panels/dom';
import { allKeyFrames, nextKeyFrame, prevKeyFrame } from './frames';

export interface TransportHandle {
  element: HTMLElement;
  /** Re-read every widget value from the store. */
  sync(): void;
  dispose(): void;
}

const FPS_PRESETS = [12, 24, 30, 60];

/** Playback-rate presets. Preview only — the clip's fps and keyframes are untouched. */
const SPEED_PRESETS = [0.25, 0.5, 1, 1.5, 2];

/**
 * A transport button. `glyph` is an icon name (design rule 5: no text-only actions); passing a
 * string instead keeps a labelled button for the two keying actions, which need their name.
 */
function button(glyph: IconName | { text: string }, tl: string, title: string): HTMLButtonElement {
  const el = document.createElement('button');
  el.type = 'button';
  el.className = typeof glyph === 'string' ? 'tl-btn tl-icon' : 'tl-btn';
  el.title = title;
  el.dataset.tl = tl;
  if (typeof glyph === 'string') el.appendChild(icon(glyph));
  else el.textContent = glyph.text;
  return el;
}

/** Swap the icon inside an icon-only button (play ⇄ pause). */
function setIcon(target: HTMLButtonElement, name: IconName): void {
  target.replaceChildren(icon(name));
}

function numberInput(tl: string, width: string, min?: number): HTMLInputElement {
  const el = document.createElement('input');
  el.type = 'number';
  el.className = 'tl-num';
  el.dataset.tl = tl;
  el.style.width = width;
  if (min !== undefined) el.min = String(min);
  return el;
}

/**
 * A two-state icon button (loop, snap). `aria-pressed` carries the state for assistive tech and
 * `.on` paints it blue — the same "on = UI state" rule the viewport switches follow (rule 2).
 * A labelled checkbox costs ~60px each, which the 1280px row does not have.
 */
function toggle(glyph: IconName, tl: string, title: string): HTMLButtonElement {
  const node = button(glyph, tl, title);
  node.setAttribute('aria-pressed', 'false');
  return node;
}

function setPressed(target: HTMLButtonElement, on: boolean): void {
  target.setAttribute('aria-pressed', String(on));
  target.classList.toggle('on', on);
}

function select(tl: string, options: readonly { value: string; label: string }[]): HTMLSelectElement {
  const el = document.createElement('select');
  el.className = 'tl-select';
  el.dataset.tl = tl;
  for (const option of options) {
    const node = document.createElement('option');
    node.value = option.value;
    node.textContent = option.label;
    el.appendChild(node);
  }
  return el;
}

function label(text: string): HTMLSpanElement {
  const el = document.createElement('span');
  el.className = 'tl-label';
  el.textContent = text;
  return el;
}

/** Label and value in one recessed slot — the transport half of design rule 4. */
function slot(...parts: HTMLElement[]): HTMLSpanElement {
  const el = document.createElement('span');
  el.className = 'tl-slot';
  el.append(...parts);
  return el;
}

export function mountTransport(container: HTMLElement, ctx: EditorContext): TransportHandle {
  const store = ctx.store;
  const element = document.createElement('div');
  element.className = 'tl-transport';

  const first = button('skipBack', 'first', 'Go to frame 0 (Home)');
  const prev = button('stepBack', 'prev-key', 'Previous keyframe (,)');
  const play = button('play', 'play', 'Play / pause (Space)');
  const next = button('stepForward', 'next-key', 'Next keyframe (.)');
  const last = button('skipForward', 'last', 'Go to the last frame (End)');
  play.classList.add('tl-play');

  const frame = numberInput('frame', '3.4em', 0);
  const fps = select('fps', [
    ...FPS_PRESETS.map((value) => ({ value: String(value), label: `${value} fps` })),
    { value: 'custom', label: 'custom…' },
  ]);
  const fpsCustom = numberInput('fps-custom', '3.4em', 1);
  fpsCustom.hidden = true;
  const duration = numberInput('duration', '3.4em', 1);
  const speed = select(
    'speed',
    SPEED_PRESETS.map((value) => ({ value: String(value), label: `${value}×` })),
  );

  const loop = toggle('loop', 'loop', 'Loop playback');
  const snap = toggle('magnet', 'snap', 'Snap dragged keys to whole frames');

  const easing = select('easing', [
    { value: '', label: '—' },
    ...TWEEN_PRESETS.map((preset) => ({ value: preset, label: preset })),
  ]);

  const keyBone = button({ text: 'Key bone' }, 'key-bone', 'Key the selected bone (K)');
  const keyAll = button({ text: 'Key all posed' }, 'key-all', 'Key every posed bone (Shift+K)');
  keyBone.prepend(icon('key'));
  keyAll.prepend(icon('key'));

  const zoom = document.createElement('input');
  zoom.type = 'range';
  zoom.className = 'tl-zoom';
  zoom.dataset.tl = 'zoom';
  zoom.min = String(TIMELINE_ZOOM_MIN);
  zoom.max = String(TIMELINE_ZOOM_MAX);
  zoom.step = '0.1';

  // Labels are short because the slot's own "show" label already supplies the noun, and a
  // `<select>` is as wide as its longest option — "Selected only" alone cost 45px of the row.
  const bones = select('bones', [
    { value: 'all', label: 'All' },
    { value: 'keyed', label: 'Keyed' },
    { value: 'selected', label: 'Selected' },
  ]);
  bones.title = 'Which bone tracks the sheet shows';

  // Which clip the sheet below belongs to — the mockup's right-hand transport readout.
  const clipName = document.createElement('span');
  clipName.className = 'tl-clip';
  clipName.dataset.tl = 'clip-name';
  // Its own element, not a bare text node: `text-overflow` has nothing to clip otherwise.
  const clipNameText = document.createElement('span');
  clipName.append(icon('film'), clipNameText);

  const spacer = document.createElement('span');
  spacer.className = 'tl-spacer';

  // Left → right: WHERE (playback + playhead), WHAT (clip shape), HOW (keying), then the view
  // controls after the spacer. Contract C12 — the `data-tl` names are the frozen hooks.
  element.append(
    first,
    prev,
    play,
    next,
    last,
    slot(label('frame'), frame),
    slot(label('fps'), fps, fpsCustom),
    slot(label('length'), duration),
    slot(label('speed'), speed),
    loop,
    snap,
    keyBone,
    keyAll,
    slot(label('easing'), easing),
    spacer,
    slot(label('show'), bones),
    slot(label('zoom'), zoom),
    clipName,
  );
  container.appendChild(element);

  // ---------------------------------------------------------------------------
  // Wiring
  // ---------------------------------------------------------------------------

  const listeners: Array<() => void> = [];
  function on<K extends keyof HTMLElementEventMap>(
    target: HTMLElement,
    type: K,
    handler: (event: HTMLElementEventMap[K]) => void,
  ): void {
    target.addEventListener(type, handler as EventListener);
    listeners.push(() => target.removeEventListener(type, handler as EventListener));
  }

  on(first, 'click', () => store.actions.setPlayhead(0));
  on(last, 'click', () => store.actions.setPlayhead(store.getState().clip?.durationFrames ?? 0));
  on(play, 'click', () => store.actions.togglePlay());
  on(prev, 'click', () => {
    const state = store.getState();
    const target = prevKeyFrame(allKeyFrames(state.clip), Math.round(state.playhead.frame));
    if (target !== null) store.actions.setPlayhead(target);
  });
  on(next, 'click', () => {
    const state = store.getState();
    const target = nextKeyFrame(allKeyFrames(state.clip), Math.round(state.playhead.frame));
    if (target !== null) store.actions.setPlayhead(target);
  });

  on(frame, 'change', () => {
    const value = Number(frame.value);
    if (Number.isFinite(value)) store.actions.setPlayhead(Math.round(value));
    sync();
  });

  on(fps, 'change', () => {
    if (fps.value === 'custom') {
      fpsCustom.hidden = false;
      fpsCustom.value = String(store.getState().clip?.fps ?? 30);
      fpsCustom.focus();
      return;
    }
    store.actions.setFps(Number(fps.value));
    sync();
  });
  on(fpsCustom, 'change', () => {
    const value = Number(fpsCustom.value);
    if (Number.isFinite(value)) store.actions.setFps(value);
    sync();
  });

  on(duration, 'change', () => {
    const value = Number(duration.value);
    if (Number.isFinite(value)) store.actions.setDuration(Math.round(value));
    // A rejected duration toasts and leaves the clip alone; put the real value back.
    sync();
  });

  on(speed, 'change', () => {
    const value = Number(speed.value);
    if (Number.isFinite(value)) store.actions.setPlaybackSpeed(value);
    sync();
  });

  on(loop, 'click', () => store.actions.setLoop(!store.getState().playhead.loop));
  on(snap, 'click', () => store.actions.setSnap(!store.getState().timeline.snap));

  on(easing, 'change', () => {
    if (!easing.value) return;
    store.actions.setEasingForSelection(easing.value as TweenPreset);
    sync();
  });

  on(keyBone, 'click', () => {
    // With a bone selected this always writes a key (a hold key when nothing was posed); with
    // nothing selected it falls back to "key every posed bone", which toasts when there is
    // nothing to key rather than doing nothing at all (wave-5 review finding 8).
    const bone = store.getState().selection.bone;
    if (bone) store.actions.keyBone(bone);
    else store.actions.keyAllPosed();
  });
  on(keyAll, 'click', () => store.actions.keyAllPosed());

  on(zoom, 'input', () => store.actions.setZoom(Number(zoom.value)));
  on(bones, 'change', () => store.actions.setVisibleBones(bones.value as VisibleBonesFilter));

  // ---------------------------------------------------------------------------
  // State → widgets
  // ---------------------------------------------------------------------------

  function sync(): void {
    const state = store.getState();
    const clip = state.clip;
    const enabled = state.mode === 'animate' && !!clip;
    element.classList.toggle('tl-disabled', !enabled);

    setIcon(play, state.playhead.playing ? 'pause' : 'play');
    play.setAttribute('aria-pressed', String(state.playhead.playing));
    clipNameText.textContent = clip ? clip.name : 'no clip';
    clipName.title = clip ? clip.name : '';

    if (document.activeElement !== frame) frame.value = String(Math.round(state.playhead.frame));
    frame.max = String(clip?.durationFrames ?? 0);

    const currentFps = clip?.fps ?? 30;
    if (FPS_PRESETS.includes(currentFps)) {
      fps.value = String(currentFps);
      fpsCustom.hidden = true;
    } else {
      fps.value = 'custom';
      fpsCustom.hidden = false;
      if (document.activeElement !== fpsCustom) fpsCustom.value = String(currentFps);
    }

    if (document.activeElement !== duration) duration.value = String(clip?.durationFrames ?? 0);

    speed.value = String(state.playhead.speed);

    setPressed(loop, state.playhead.loop);
    setPressed(snap, state.timeline.snap);
    easing.value = selectSelectionEasing(state) ?? '';
    easing.disabled = !enabled || state.keySelection.length === 0;
    zoom.value = String(state.timeline.zoom);
    bones.value = state.timeline.visibleBones;

    // Enabled without a selection too: it keys every posed bone, or explains why it cannot.
    keyBone.disabled = !enabled;
    keyBone.title = state.selection.bone
      ? `Key ${state.selection.bone} (K)`
      : 'Key every posed bone (K)';
    keyAll.disabled = !enabled;

    for (const control of [
      first,
      prev,
      play,
      next,
      last,
      loop,
      snap,
      frame,
      fps,
      fpsCustom,
      duration,
      speed,
      zoom,
      bones,
    ]) {
      control.disabled = !enabled;
    }
  }

  sync();

  return {
    element,
    sync,
    dispose(): void {
      for (const off of listeners) off();
      listeners.length = 0;
      element.remove();
    },
  };
}
